import { Text, COLORS } from "@asayinc/component-library";
import {
  FilterList,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Collapse,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { PageContainer } from "src/components/containers/PageContainer/PageContainer";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import {
  SecuritiesTagList,
  SecurityDisplayField,
} from "src/components/molecules/SecuritiesTagList";
import { ISSUER_TYPE_OPTIONS } from "src/constants/options/issuerOptions";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { useIssuersQuery } from "src/store/issuer/api";

const ISSUER_TYPE_LABELS: Record<string, string> = {
  corporation: "Corporation",
  investment_trust: "Investment Trust",
};

export function Issuers() {
  const navigate = useNavigate();
  const methods = useForm();
  const { type } = useTableSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const { getSort, offset, limit, ordering, onChangePage, onOrderingChange } =
    useTablePagination();

  const { data, isFetching, isLoading } = useIssuersQuery({
    offset,
    limit,
    ordering,
    type,
  });

  const issuers = data?.results || [];
  const count = data?.count || 0;
  const page = Math.floor(offset / limit);
  const totalPages = Math.ceil(count / limit);

  const handleRowClick = (id: string) => {
    navigate(`/issuers/${id}/proxy-events`);
  };

  const handleSort = (key: string) => {
    const currentSort = getSort(key);
    onOrderingChange({ sort: currentSort || "desc" }, key);
  };

  const handleFirstPage = () => onChangePage(1);
  const handlePrevPage = () => onChangePage(page);
  const handleNextPage = () => onChangePage(page + 2);
  const handleLastPage = () => onChangePage(totalPages);

  const startItem = count === 0 ? 0 : offset + 1;
  const endItem = Math.min(offset + limit, count);

  // Common cell styles to match TableV2
  const headerCellSx = {
    py: 3,
    px: 4,
    borderBottom: `1px solid ${COLORS.P_OUTLINE}`,
    backgroundColor: COLORS.WHITE,
  };

  const bodyCellSx = {
    py: 4,
    px: 4,
    borderBottom: `1px solid ${COLORS.P_OUTLINE}`,
  };

  return (
    <PageContainer>
      <Text variant="h4" sx={{ pb: 4, pt: 10, px: 10 }}>
        Issuers
      </Text>
      <ScrollContainer sx={{ pt: 4, pb: 10, pl: 10, pr: 6.25 }}>
        <Box
          sx={{
            backgroundColor: COLORS.WHITE,
            borderRadius: 2,
            border: `1px solid ${COLORS.P_OUTLINE}`,
            overflow: "hidden",
          }}
        >
          {/* Header with Title, Filter, and Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 3,
              px: 4,
              borderBottom: `1px solid ${COLORS.P_OUTLINE}`,
            }}
          >
            <Text variant="subtitle2">All Issuers</Text>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              {/* Filter Button */}
              <Box
                onClick={() => setFilterOpen(!filterOpen)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: filterOpen ? "#1968FF" : COLORS.P_MED_EMPHASIS,
                  "&:hover": {
                    color: "#1968FF",
                  },
                }}
              >
                <FilterList sx={{ fontSize: 20 }} />
                <Text
                  variant="body2"
                  sx={{
                    ml: 1,
                    fontWeight: 500,
                    color: "inherit",
                  }}
                >
                  Types
                </Text>
              </Box>

              {/* Pagination */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Text
                  variant="body2"
                  sx={{ color: COLORS.P_MED_EMPHASIS, mr: 1 }}
                >
                  {startItem}-{endItem} of {count.toLocaleString()}
                </Text>
                <IconButton
                  size="small"
                  onClick={handleFirstPage}
                  disabled={page === 0}
                  sx={{ p: 0.5 }}
                >
                  <FirstPage sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handlePrevPage}
                  disabled={page === 0}
                  sx={{ p: 0.5 }}
                >
                  <KeyboardArrowLeft sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleNextPage}
                  disabled={page >= totalPages - 1}
                  sx={{ p: 0.5 }}
                >
                  <KeyboardArrowRight sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleLastPage}
                  disabled={page >= totalPages - 1}
                  sx={{ p: 0.5 }}
                >
                  <LastPage sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Filter */}
          <Collapse in={filterOpen}>
            <Box
              sx={{
                p: 4,
                borderBottom: `1px solid ${COLORS.P_OUTLINE}`,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <FormProvider {...methods}>
                <TableMultiSelectFilter
                  fieldName={TABLE_SEARCH_PARAMS.TYPE}
                  label="Types"
                  options={ISSUER_TYPE_OPTIONS}
                  queryParameter={TABLE_SEARCH_PARAMS.TYPE}
                />
              </FormProvider>
            </Box>
          </Collapse>

          {/* Loading State */}
          {isLoading && (
            <Stack alignItems="center" justifyContent="center" py={10}>
              <CircularProgress />
            </Stack>
          )}

          {/* Table */}
          {!isLoading && (
            <Table sx={{ opacity: isFetching ? 0.5 : 1, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    <TableSortLabel
                      active={ordering === "name" || ordering === "-name"}
                      direction={getSort("name")}
                      onClick={() => handleSort("name")}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: 16,
                        },
                      }}
                    >
                      <Text
                        variant="body2"
                        sx={{ color: COLORS.P_MED_EMPHASIS }}
                      >
                        Name
                      </Text>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <Text variant="body2" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                      Ticker
                    </Text>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <Text variant="body2" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                      Cusip
                    </Text>
                  </TableCell>
                  <TableCell sx={headerCellSx}>
                    <Text variant="body2" sx={{ color: COLORS.P_MED_EMPHASIS }}>
                      Type
                    </Text>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issuers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ py: 10, border: "none" }}
                    >
                      <Text variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        No Issuers found
                      </Text>
                      <Text
                        variant="body2"
                        sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}
                      >
                        Sorry, there are no issuers that match your applied
                        filters.
                      </Text>
                    </TableCell>
                  </TableRow>
                )}
                {issuers.map((issuer) => (
                  <TableRow
                    key={issuer.id}
                    hover
                    onClick={() => handleRowClick(issuer.id)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#F5F5F5",
                      },
                    }}
                  >
                    <TableCell sx={bodyCellSx}>
                      <Text variant="body2" sx={{ fontWeight: 600 }}>
                        {issuer.name || "-"}
                      </Text>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <SecuritiesTagList
                        securities={issuer.securities || []}
                        displayField={SecurityDisplayField.TICKER}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <SecuritiesTagList
                        securities={issuer.securities || []}
                        displayField={SecurityDisplayField.CUSIP}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Text variant="body2">
                        {issuer.type
                          ? ISSUER_TYPE_LABELS[issuer.type] || issuer.type
                          : "-"}
                      </Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </ScrollContainer>
    </PageContainer>
  );
}
